import { compact } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import { type Customer, useGetCustomersQuery } from '../client/fleet-management'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { CustomerCacheRecord } from './storage/schema'

type Customers = Customer[]

const CUSTOMERS_PAGE_SIZE = 2500
const CUSTOMERS_DEPTH = 100

export type CustomersDataManagerContextValue = {
  data: Customers
  isLoading: boolean
  isFetching: boolean
  /**
   * Whether the hierarchy came from this session's API response rather than
   * from IndexedDB. `isLoading` goes false on a failed initial fetch too, so
   * it is not on its own evidence that the tree is current.
   */
  isFromApi: boolean
}

type UseCustomersOptions = {
  skip?: boolean
}

function useCustomers(options?: UseCustomersOptions) {
  const principalOrgId = usePrincipalOrgId()
  const shouldSkip = options?.skip || !principalOrgId

  // `currentData` (not `data`) is arg-scoped. Prefer it so a principal-org arg change
  // does not keep serving a previous principal's payload at the same reference.
  const { currentData, isLoading, isFetching } = useGetCustomersQuery(
    {
      organizationId: principalOrgId ?? '',
      page: 0,
      count: CUSTOMERS_PAGE_SIZE,
      depth: CUSTOMERS_DEPTH
    },
    { skip: shouldSkip }
  )

  return {
    customers: currentData?.data,
    isLoading,
    isFetching
  }
}

export default function useCustomersDataManager(service: IndexedDbService) {
  const { customers, isLoading, isFetching } = useCustomers()

  const inMemoryCustomers = useCustomersPersistence(service, customers)
  return useMemo<CustomersDataManagerContextValue>(
    () => ({
      data: inMemoryCustomers,
      isLoading,
      isFetching,
      isFromApi: customers !== undefined
    }),
    [inMemoryCustomers, isLoading, isFetching, customers]
  )
}

function useCustomersPersistence(service: IndexedDbService, customers?: Customers) {
  const [inMemoryCustomers, setInMemoryCustomers] = useState<Customers>([])
  const receivedApiDataRef = useRef(false)

  // Hydrate the customers cache from IndexedDB
  useEffect(() => {
    async function hydrateCustomers() {
      try {
        const records = await service.getAll('customers')
        if (!receivedApiDataRef.current) {
          setInMemoryCustomers(records.map((record) => record.payload))
        }
      } catch (error) {
        logger.error('Failed to hydrate customers cache', error)
      }
    }

    void hydrateCustomers()
  }, [service])

  // Persist the customers to IndexedDB
  useEffect(() => {
    if (!customers) return

    receivedApiDataRef.current = true
    setInMemoryCustomers(customers)

    const fetchedAt = Date.now()
    const records = compact(customers.map((customer) => createCustomerRecord(customer, fetchedAt)))

    const replaceRecords = async () => {
      try {
        await service.replace('customers', records)
      } catch (error) {
        logger.error('Failed to persist customers snapshot', error)
      }
    }

    void replaceRecords()
  }, [customers, service])

  return inMemoryCustomers
}

function createCustomerRecord(customer: Customer, syncedAt: number): CustomerCacheRecord | undefined {
  if (!customer.id) return undefined

  return {
    id: customer.id,
    payload: customer,
    syncedAt
  }
}
